import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    TextField,
    makeStyles,
    FormControl,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { format, isValid } from 'date-fns';
import { RichTextEditor, showToast } from '@klpkonsern/ui';

import { Heading, Block, ActionButton, Loader } from '@klp/design';
import { contextSelectors } from '@klp/contextSelector';
import { usePostKommentarMutation } from '../services/rtkApi';
import {
    kommentarerSelectorActions,
    kommentarerSelectors,
} from '../store/kommentarerSlice';

type NyKommentarProps = {
    onCancel: () => void;
    onSave: () => void;
};

const useStyles = makeStyles(() => ({
    titleContainer: {
        width: '100%',
    },
    title: {
        width: 'inherit',
    },
    datePicker: {
        maxWidth: '300px',
    },
}));

export const NyKommentar = ({ onCancel, onSave }: NyKommentarProps) => {
    const { t } = useTranslation();
    const { selectedDate: kommentarerSelectedDate, postMode } =
        useSelector(kommentarerSelectors);
    const { selectedContext } = useSelector(contextSelectors);
    const [text, setText] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [errors, setErrors] = useState<{ text: boolean; title: boolean }>({
        text: false,
        title: false,
    });
    const [selectedDate, setSelectedDate] = useState(kommentarerSelectedDate);
    const classes = useStyles();
    const dispatch = useDispatch();

    const {
        reducerActions: { setPostMode, setEditMode },
    } = kommentarerSelectorActions;

    const handleDateChange = (date: Date) => {
        if (!isValid(date)) {
            return;
        }
        setSelectedDate(date);
    };
    const { pathname } = useLocation();

    const [framhevetKommentar, setFremhevetKommentar] =
        useState<boolean>(false);

    const [
        postKommentar,
        { isError: postKommentarError, isLoading: postKommentarLoading },
    ] = usePostKommentarMutation();

    const handlePostKommentar = async () => {
        if (!selectedContext?.id) return;

        await postKommentar({
            byggNummer: selectedContext?.id,
            kategori: pathname.substring(1),
            tittel: title,
            framhevet: framhevetKommentar,
            tekst: text,
            gjelderForAar: format(selectedDate, 'yyyy'),
        });

        if (postKommentarError) {
            showToast({ body: t('general.error.message'), type: 'error' });
        }
    };

    const handleOpen = () => {
        dispatch(setPostMode(true));
        dispatch(setEditMode({ editFlag: false, kommentarId: '' }));
    };

    const handleClose = () => {
        dispatch(setPostMode(false));
        if (onCancel) {
            onCancel();
        }
    };

    const handleSave = () => {
        handlePostKommentar();
        dispatch(setPostMode(false));
        if (onSave) {
            onSave();
        }
    };

    useEffect(() => {
        if (title?.length > 50 || title?.length < 1) {
            setErrors({ ...errors, title: true });
        } else {
            setErrors({ ...errors, title: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title]);

    useEffect(() => {
        setSelectedDate(kommentarerSelectedDate);
    }, [kommentarerSelectedDate]);

    return (
        <>
            {!postMode ? (
                <Block paddingTop="fat">
                    <ActionButton
                        actionType="forward"
                        translation="comments.newComment"
                        onClick={handleOpen}
                        data-cy="CommentsView_AddComment"
                    />
                </Block>
            ) : (
                <Block paddingRight="compact">
                    <Loader isLoading={postKommentarLoading}>
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="comments.newComment"
                            data-cy="AddCommentView__Title"
                        />
                        <Block
                            className={classes.titleContainer}
                            paddingTop="compact"
                            paddingBottom="fat"
                        >
                            <TextField
                                className={classes.title}
                                required={true}
                                id="outlined-required"
                                label={t('comments.titleLabel')}
                                onChange={(e) => setTitle(e.target.value)}
                                error={errors.title}
                                helperText={
                                    errors.title &&
                                    t('comments.validation.titleOutOfRange')
                                }
                            />
                        </Block>
                        <Block
                            className={classes.datePicker}
                            paddingTop="tiny"
                            paddingBottom="compact"
                        >
                            <KeyboardDatePicker
                                fullWidth={true}
                                allowKeyboardControl={true}
                                views={['year']}
                                label={t('comments.chooseYearDescription')}
                                variant="inline"
                                inputVariant="outlined"
                                InputAdornmentProps={{
                                    position: 'end',
                                }}
                                value={selectedDate}
                                onChange={(date: Date | null) => {
                                    if (date) handleDateChange(date);
                                }}
                                maxDate={new Date()}
                                autoOk={true}
                            />
                        </Block>
                        <RichTextEditor
                            onContentChange={(content) => setText(content)}
                            enableSourceEditing={true}
                            enableTables={true}
                        />
                        <Block paddingTop="compact">
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={framhevetKommentar}
                                            onChange={(e) =>
                                                setFremhevetKommentar(
                                                    e.target.checked,
                                                )
                                            }
                                            name="comments.highlight"
                                        />
                                    }
                                    label={t('comments.highlight')}
                                />
                            </FormControl>
                        </Block>

                        <Grid container={true} justifyContent="flex-end">
                            <Block paddingTop="compact" paddingBottom="fat">
                                <Grid item={true}>
                                    <ActionButton
                                        disabled={errors.title}
                                        actionType="save"
                                        translation="general.save"
                                        onClick={handleSave}
                                    />
                                </Grid>
                            </Block>
                            <Block
                                paddingTop="compact"
                                paddingLeft="compact"
                                paddingBottom="fat"
                            >
                                <Grid item={true}>
                                    <ActionButton
                                        actionType="cancel"
                                        onClick={handleClose}
                                        translation="general.cancel"
                                    />
                                </Grid>
                            </Block>
                        </Grid>
                    </Loader>
                </Block>
            )}
        </>
    );
};
